// The lane light of mockup round 139 card 20 (R3072, R2723;
// ui-map-interface.md §6.1.14 "The lane light", v2-the-deep.md §11.3).
//
// The owner picked card 20 Lit Road of round 139 for the shipping lane and
// its lane light, and the chart reading picks the look: in the modern
// reading (and under the setting `off`) a white star of four rays in a
// glow, with the italic light character of round 115 card 01; in the
// historical reading the engraved star of sixteen points of card 01. The
// road of chart card 16 shows the reach of the light (the lit stretch), so
// the light throws no burn of its own and holds still. The AUTO deployable
// look `terrain-kin` draws its lane light here; a look the player picks in
// the workshop keeps its own.
//
// A painter draws ONE lane light at the origin, one unit one map px, in
// the side ink it is handed. The figures are the `light_*` keys of
// `[chart.piece.shipping_lanes]` in `data/presentation/chart.toml`, so one
// table tunes the whole set. The words come from the caller.
//
// This is a leaf (`leaves.test.ts`): it paints into a context it is handed.
//
// The ghost light of a lane gap on chart card 16 (R3074 rule 14) is the
// ghost of the guide as ONE atlas cell a reading and an ink
// (`ghostLightKey`), so the chart packs it in its quad batch (§10).

import { CHART } from '../gen/presentation';
import type { AtlasSpec } from '../fast/gfx/atlas';
import { SEA_INK, type Ctx } from './seaobject-kit';
import { TAU, clamp01 } from './math';
import { rgba } from './color';

/** Which light this is to the viewer: the own, a faction mate's or a
 *  hostile one the picture holds as known. */
export type LightSide = 'own' | 'mate' | 'foe';

/** What one lane light painter is told. */
export interface LaneLightMark {
  /** The side ink. */
  col: number;
  side: LightSide;
  /** The anchor holds and the light acts (`SeaObject.armed`). */
  armed: boolean;
  /** The share of the anchor time that passed, 0 to 1, while it anchors. */
  anchor: number;
  /** The map zoom: the light character stands from `char_zoom` up. */
  z: number;
  historical: boolean;
}

/** The words and the face of the light character. */
export interface LaneLightText {
  modern: string;
  historical: string;
  /** The Fell family of the chart (`CHART_FELL_FAMILY`). */
  fell: string;
}

const T = CHART.pieces.shipping_lanes;
const WHITE = SEA_INK.white;

/** The ring of the anchor and the arc of the anchor time that passed. */
function anchorRing(c: Ctx, r: number, frac: number, col: number): void {
  const f = T.fig;
  c.beginPath(); c.arc(0, 0, r, 0, TAU);
  c.lineWidth = f.anchor_ring_width; c.strokeStyle = rgba(col, f.anchor_ring_alpha); c.stroke();
  if (!(frac > 0)) return;
  c.beginPath(); c.arc(0, 0, r, -Math.PI / 2, -Math.PI / 2 + TAU * clamp01(frac));
  c.lineWidth = f.anchor_arc_width; c.lineCap = 'round'; c.strokeStyle = rgba(col, f.anchor_arc_alpha); c.stroke();
}

/** A soft disc: the glow of the set, full at the middle and gone at `r`. */
function glow(c: Ctx, x: number, y: number, r: number, col: number, a: number): void {
  if (!(r > 0) || !(a > 0)) return;
  const g = c.createRadialGradient(x, y, 0, x, y, r);
  const at = CHART.pieces.fairways.arr.glow_stops, al = CHART.pieces.fairways.arr.glow_stop_alpha;
  for (let i = 0; i < at.length; i++) g.addColorStop(at[i], rgba(col, a * (al[i] ?? 0)));
  c.fillStyle = g; c.beginPath(); c.arc(x, y, r, 0, TAU); c.fill();
}

/** The light character beside the light, from its zoom up. */
function lightChar(c: Ctx, text: string, fell: string, px: number, dx: number, dy: number, col: number, a: number): void {
  c.font = `italic 400 ${px}px ${fell}`;
  c.textAlign = 'left'; c.textBaseline = 'middle';
  c.fillStyle = rgba(col, a);
  c.fillText(text, dx, dy);
}

/** Card 20: a white star of four rays in its glow, dimmer while it
 *  anchors; a hostile star stands in its own ink. */
function modern(c: Ctx, o: LaneLightMark, w: LaneLightText): void {
  const f = T.modern.fig;
  const m = o.side === 'mate' ? f.mate_mul : 1;
  const acting = o.armed;
  glow(c, 0, 0, f.glow_px, o.col, acting ? f.light_glow_alpha * m : f.anchor_glow_alpha);
  const ink = o.side === 'foe' ? o.col : WHITE;
  const a = acting ? f.light_alpha : f.anchor_alpha;
  c.fillStyle = rgba(ink, a);
  for (let i = 0; i < 4; i++) {
    c.save(); c.rotate((i * Math.PI) / 2 + Math.PI / 4);
    c.beginPath(); c.moveTo(0, -f.ray_px); c.lineTo(f.ray_half, 0); c.lineTo(0, f.ray_px); c.lineTo(-f.ray_half, 0); c.closePath();
    c.fill(); c.restore();
  }
  if (!acting) anchorRing(c, f.ring_px, o.anchor, o.col);
  if (o.z >= f.char_zoom) lightChar(c, w.modern, w.fell, f.char_px, f.char_dx, f.char_dy, o.col, f.char_alpha);
}

/** Card 01 (and 20): an engraved star of sixteen points, filled when it
 *  acts and hollow while it anchors. */
function historical(c: Ctx, o: LaneLightMark, w: LaneLightText): void {
  const f = T.historical.fig;
  const acting = o.armed;
  const a = o.side === 'mate' ? f.light_mate_alpha : f.light_alpha;
  c.beginPath();
  for (let i = 0; i < 16; i++) {
    const r = i % 4 === 0 ? f.star_outer : i % 2 === 0 ? f.star_mid : f.star_inner;
    const th = (i * Math.PI) / 8 - Math.PI / 2;
    if (i) c.lineTo(Math.cos(th) * r, Math.sin(th) * r); else c.moveTo(Math.cos(th) * r, Math.sin(th) * r);
  }
  c.closePath();
  if (acting) { c.fillStyle = rgba(o.col, a); c.fill(); }
  else { c.lineWidth = f.hollow_width; c.lineJoin = 'round'; c.strokeStyle = rgba(o.col, f.hollow_alpha); c.stroke(); }
  if (o.z >= f.char_zoom) lightChar(c, w.historical, w.fell, f.char_px, f.char_dx, f.char_dy, o.col, f.char_alpha);
  if (!acting) anchorRing(c, f.ring_px, o.anchor, o.col);
}

/** Paints one lane light of the set in its reading. */
export function paintLaneLight(c: Ctx, o: LaneLightMark, w: LaneLightText): void {
  if (o.historical) historical(c, o, w);
  else modern(c, o, w);
}

/**
 * Paints the ghost of a lane light at a chain point (R3066, the guide of
 * the chain): the light of the set in its reading, acting, at alpha `a`,
 * with no light character, so it never paints again with the clock.
 */
export function paintGhostLight(c: Ctx, col: number, historical: boolean, a: number, w: LaneLightText): void {
  if (!(a > 0)) return;
  c.save(); c.globalAlpha *= a;
  paintLaneLight(c, { col, side: 'own', armed: true, anchor: 1, z: -Infinity, historical }, w);
  c.restore();
}

/** The supersampling of a ghost light cell: the light is a few px across. */
const GHOST_CELL_SCALE = 4;

/** The half extent of a ghost light cell, world px: the glow of card 20 or
 *  the star of card 01, the larger. */
const GHOST_CELL_HALF = Math.ceil(Math.max(T.modern.fig.glow_px, T.historical.fig.star_outer)) + 1;

/** No light character: a ghost writes none. */
const NO_WORDS: LaneLightText = { modern: '', historical: '', fell: '' };

/** The key of the atlas cell of a ghost light in a reading and an ink:
 *  `llg:<m|h>:<ink>`. The quad tints it white and sets its alpha. */
export function ghostLightKey(historical: boolean, col: number): string {
  return `llg:${historical ? 'h' : 'm'}:${col}`;
}

/** The cell of a `llg:` key: the ghost light of `paintGhostLight` at full
 *  alpha. Null for any other key. */
export function ghostLightSpecFor(key: string): AtlasSpec | null {
  const p = key.split(':');
  if (p.length !== 3 || p[0] !== 'llg' || (p[1] !== 'm' && p[1] !== 'h')) return null;
  const col = Number(p[2]);
  if (!Number.isInteger(col) || col < 0 || col > 0xffffff) return null;
  const h = GHOST_CELL_HALF, historical = p[1] === 'h';
  return {
    key,
    scale: GHOST_CELL_SCALE,
    box: { x: -h, y: -h, w: 2 * h, h: 2 * h },
    draw: (ctx) => paintGhostLight(ctx, col, historical, 1, NO_WORDS),
  };
}
